import type { PayloadAction, Slice } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { SessionMentor } from '../../types/mentor';

export interface Avatar {
  id: string;
  videos: AvatarVideo[];
}

export interface AvatarVideo {
  original_video_url: string;
  preview_url?: string;
  video_type: string;
}

export interface Artifact {
  id: string;
  artifactName: string;
  iconName: string;
  iconDescription: string;
}

export interface WebRTCStatus {
  blocked: boolean;
  reason?: string;
  message?: string;
  errorStatus?: number;
  btnType?: string;
}

export interface SessionState {
  avatarReady: boolean;
  companionSlug: string | null;
  secondsBalance: number | null;
  creditBalance: number | null;
  companionData: SessionMentor | null;
  transcriptTime: number | null;
  dataChannel: RTCDataChannel | null;
  sessionId: string | undefined;
  webRTCStatus: WebRTCStatus;
  isFileUploading: boolean;
  taskId: string | null;
  isDesignerSession: boolean;
  isSessionPage: boolean;
  isConnecting: boolean;
  autoConnect: boolean;
  socketId: string | null;
  prevMentorId: string | null;
  prevSocketId: string | null;
  promptData: string | null;
  isTwinUpdating: boolean;
}

export type VisualElement = {
  url: string;
  title: string;
  previewUrl?: string;
};

export type CompositeDataItem = {
  script: string;
  visualElements?: VisualElement[];
};

export interface Voice {
  id: string;
  provider: string;
}

export type CompositeData = CompositeDataItem[];

const initialState: SessionState = {
  sessionId: undefined,
  companionSlug: null,
  companionData: null,
  secondsBalance: null,
  creditBalance: null,
  transcriptTime: null,
  avatarReady: false,
  dataChannel: null,
  webRTCStatus: { blocked: false },
  isFileUploading: false,
  taskId: null,
  isDesignerSession: false,
  isSessionPage: false,
  isConnecting: false,
  autoConnect: false,
  socketId: null,
  prevMentorId: null,
  prevSocketId: null,
  promptData: null,
  isTwinUpdating: false,
};

export const sessionSlice: Slice<SessionState> = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setDataChannel: (state, action: PayloadAction<RTCDataChannel>) => {
      state.dataChannel = action.payload;
    },
    setSessionId: (state, action: PayloadAction<string | undefined>) => {
      state.sessionId = action.payload;
    },
    setAvatarReady: (state, action: PayloadAction<boolean>) => {
      state.avatarReady = action.payload;
    },
    setWebRTCStatus: (state, action: PayloadAction<WebRTCStatus>) => {
      state.webRTCStatus = action.payload;
    },
    setIsFileUploading: (state, action: PayloadAction<boolean>) => {
      state.isFileUploading = action.payload;
    },
    setCompanionSlug: (
      state,
      action: PayloadAction<{
        companionSlug: string | null;
        isSessionPage: boolean;
        autoConnect?: boolean;
      }>,
    ) => {
      return {
        ...initialState,
        ...action.payload,
        autoConnect: action.payload.autoConnect || false,
        prevMentorId: state.prevMentorId,
        prevSocketId: state.prevSocketId,
        secondsBalance: state.secondsBalance,
        creditBalance: state.creditBalance,
      };
    },
    setSecondsBalance: (state, action: PayloadAction<number | null>) => {
      state.secondsBalance = action.payload;
    },
    setPromptData: (state, action: PayloadAction<string>) => {
      state.promptData = action.payload;
    },
    setCreditBalance: (state, action: PayloadAction<number | null>) => {
      state.creditBalance = action.payload;
    },
    setCompanionData: (state, action: PayloadAction<SessionMentor | null>) => {
      state.companionData = action.payload;
    },
    setTranscriptTime: (state, action: PayloadAction<number | null>) => {
      state.transcriptTime = action.payload;
    },
    setTaskId: (state, action: PayloadAction<string | null>) => {
      state.taskId = action.payload;
    },
    setIsDesignerSession: (state, action: PayloadAction<boolean>) => {
      state.isDesignerSession = action.payload;
    },
    setIsConnecting: (state, action: PayloadAction<boolean>) => {
      state.isConnecting = action.payload;
    },
    setAutoConnect: (state, action: PayloadAction<boolean>) => {
      state.autoConnect = action.payload;
    },
    setSocketId: (state, action: PayloadAction<string | null>) => {
      state.socketId = action.payload;
    },
    setPrevMentorId: (state, action: PayloadAction<string | null>) => {
      state.prevMentorId = action.payload;
    },
    setPrevSocketId: (state, action: PayloadAction<string | null>) => {
      state.prevSocketId = action.payload;
    },
    setIsTwinUpdating: (state, action: PayloadAction<boolean>) => {
      state.isTwinUpdating = action.payload;
    },
    resetSessionState: (state, action: PayloadAction<string | null>) => {
      if (action.payload) {
        return {
          ...initialState,
          secondsBalance: state.secondsBalance,
          creditBalance: state.creditBalance,
        };
      } else {
        return {
          ...initialState,
          prevMentorId: state.prevMentorId,
          prevSocketId: state.prevSocketId,
          secondsBalance: state.secondsBalance,
          creditBalance: state.creditBalance,
        };
      }
    },
  },
});

export const {
  setDataChannel,
  setSessionId,
  setAvatarReady,
  setPersonaData,
  setWebRTCStatus,
  setSecondsBalance,
  setCreditBalance,
  isFileUploading,
  setIsFileUploading,
  setVoicesList,
  setCompanionSlug,
  setCompanionData,
  setTranscriptTime,
  setTaskId,
  setIsDesignerSession,
  resetSessionState,
  setIsConnecting,
  setAutoConnect,
  setSocketId,
  setPrevMentorId,
  setPrevSocketId,
  setPromptData,
  setIsTwinUpdating,
} = sessionSlice.actions;

export default sessionSlice.reducer;
